import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Check, ChevronRight, Circle } from "lucide-react";
import { cn } from "../lib/cn";

// ----------------------------------------------------------------------------
// Sibling-aware submenu close
//
// Radix's `SubTrigger` uses a "safe-intent" grace window: when the pointer
// leaves the trigger heading toward the submenu, the submenu stays open for
// a beat so the user can travel into it without it snapping shut. The cost
// is that moving the pointer to a sibling item leaves the submenu visibly
// open for a moment, even though the user is clearly heading away. In a
// menu with multiple items + submenus, this reads as a janky "two things
// look active at once".
//
// We fix this generically here so consumers don't have to manually wire
// `onPointerEnter` + controlled state on every sibling. Each
// `DropdownMenuContent` / `DropdownMenuSubContent` provides a registry
// scoped to its own level. Every `DropdownMenuSub` rendered inside
// registers a "close me" callback with the nearest registry. Items
// (`DropdownMenuItem` and the checkbox/radio variants) call `closeAll()`
// on pointer enter; `DropdownMenuSubTrigger` calls `closeAll(exceptSelf)`
// so hovering a trigger closes its siblings without closing its own
// submenu. Levels are scoped, so hovering inside a submenu doesn't close
// its parent.
// ----------------------------------------------------------------------------

type SubMenuRegistry = {
  register: (id: string, close: () => void) => () => void;
  closeAll: (exceptId?: string) => void;
};
const SubMenuRegistryContext = React.createContext<SubMenuRegistry | null>(null);

// Each DropdownMenuSub provides its own id to its descendants so the
// SubTrigger can ask the parent registry to "close everyone except me".
const SubMenuIdContext = React.createContext<string | null>(null);
function useFreshSubMenuRegistry() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Map();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const subMenusRef = React.useRef(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      register: (id, close) => {
        subMenusRef.current.set(id, close);
        return () => {
          subMenusRef.current.delete(id);
        };
      },
      closeAll: exceptId => {
        subMenusRef.current.forEach((close_0, id_0) => {
          if (id_0 !== exceptId) {
            close_0();
          }
        });
      }
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
const DropdownMenu = DropdownMenuPrimitive.Root;
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
const DropdownMenuGroup = DropdownMenuPrimitive.Group;
const DropdownMenuPortal = DropdownMenuPrimitive.Portal;
const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

// Compose two event handlers without dropping the consumer's handler. If the
// consumer's handler calls preventDefault we still run ours, since closing a
// stale submenu shouldn't be cancellable from a sibling.
function composeEventHandlers<E extends React.SyntheticEvent>(ours: (event: E) => void, theirs: ((event: E) => void) | undefined) {
  return (event: E) => {
    theirs?.(event);
    ours(event);
  };
}
const DropdownMenuSub = t0 => {
  const $ = _c(26);
  let children;
  let controlledOpen;
  let defaultOpen;
  let onOpenChange;
  let props;
  if ($[0] !== t0) {
    ({
      open: controlledOpen,
      defaultOpen,
      onOpenChange,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = controlledOpen;
    $[3] = defaultOpen;
    $[4] = onOpenChange;
    $[5] = props;
  } else {
    children = $[1];
    controlledOpen = $[2];
    defaultOpen = $[3];
    onOpenChange = $[4];
    props = $[5];
  }
  const parentRegistry = React.useContext(SubMenuRegistryContext);
  const id = React.useId();
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen ?? false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const onOpenChangeRef = React.useRef(onOpenChange);
  let t1;
  let t2;
  if ($[6] !== onOpenChange) {
    t1 = () => {
      onOpenChangeRef.current = onOpenChange;
    };
    t2 = [onOpenChange];
    $[6] = onOpenChange;
    $[7] = t1;
    $[8] = t2;
  } else {
    t1 = $[7];
    t2 = $[8];
  }
  React.useEffect(t1, t2);
  let t3;
  let t4;
  if ($[9] !== id || $[10] !== isControlled || $[11] !== parentRegistry) {
    t3 = () => {
      if (!parentRegistry) {
        return;
      }
      return parentRegistry.register(id, () => {
        if (isControlled) {
          onOpenChangeRef.current?.(false);
        } else {
          setInternalOpen(false);
        }
      });
    };
    t4 = [parentRegistry, id, isControlled];
    $[9] = id;
    $[10] = isControlled;
    $[11] = parentRegistry;
    $[12] = t3;
    $[13] = t4;
  } else {
    t3 = $[12];
    t4 = $[13];
  }
  React.useEffect(t3, t4);
  let t5;
  if ($[14] !== id || $[15] !== isControlled || $[16] !== parentRegistry) {
    t5 = next => {
      if (next && parentRegistry) {
        parentRegistry.closeAll(id);
      }
      if (isControlled) {
        onOpenChangeRef.current?.(next);
      } else {
        setInternalOpen(next);
      }
    };
    $[14] = id;
    $[15] = isControlled;
    $[16] = parentRegistry;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const handleOpenChange = t5;
  let t6;
  if ($[18] !== children || $[19] !== handleOpenChange || $[20] !== open || $[21] !== props) {
    t6 = <DropdownMenuPrimitive.Sub open={open} onOpenChange={handleOpenChange} {...props}>{children}</DropdownMenuPrimitive.Sub>;
    $[18] = children;
    $[19] = handleOpenChange;
    $[20] = open;
    $[21] = props;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== id || $[24] !== t6) {
    t7 = <SubMenuIdContext.Provider value={id}>{t6}</SubMenuIdContext.Provider>;
    $[23] = id;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  return t7;
};
const DropdownMenuSubTrigger = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
  inset?: boolean;
}>((t0, ref) => {
  const $ = _c(22);
  let children;
  let className;
  let inset;
  let onPointerEnter;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      inset,
      children,
      onPointerEnter,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = inset;
    $[4] = onPointerEnter;
    $[5] = props;
  } else {
    children = $[1];
    className = $[2];
    inset = $[3];
    onPointerEnter = $[4];
    props = $[5];
  }
  const registry = React.useContext(SubMenuRegistryContext);
  const ownId = React.useContext(SubMenuIdContext);
  const t1 = inset && "pl-8";
  let t2;
  if ($[6] !== className || $[7] !== t1) {
    t2 = cn("flex cursor-default select-none items-center rounded-xs px-2 py-1.5 text-sm outline-hidden focus:outline-none focus-visible:outline-none text-neutral-900 focus:bg-neutral-50 data-[highlighted]:bg-neutral-50 dark:text-white dark:focus:bg-[#444545] dark:data-[highlighted]:bg-[#444545]", t1, className);
    $[6] = className;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== ownId || $[10] !== registry) {
    t3 = event => {
      if (event.pointerType === "touch") {
        return;
      }
      registry?.closeAll(ownId ?? undefined);
    };
    $[9] = ownId;
    $[10] = registry;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== onPointerEnter || $[13] !== t3) {
    t4 = composeEventHandlers(t3, onPointerEnter);
    $[12] = onPointerEnter;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ChevronRight className="ml-auto h-4 w-4" />;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== children || $[17] !== props || $[18] !== ref || $[19] !== t2 || $[20] !== t4) {
    t6 = <DropdownMenuPrimitive.SubTrigger ref={ref} className={t2} onPointerEnter={t4} {...props}>{children}{t5}</DropdownMenuPrimitive.SubTrigger>;
    $[16] = children;
    $[17] = props;
    $[18] = ref;
    $[19] = t2;
    $[20] = t4;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  return t6;
});
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;
const DropdownMenuSubContent = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.SubContent>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>>((t0, ref) => {
  const $ = _c(12);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const registry = useFreshSubMenuRegistry();
  let t1;
  if ($[3] !== className) {
    t1 = cn("z-[9999] min-w-[8rem] overflow-visible rounded-lg border border-[#0c0c0d14] bg-white p-1 text-neutral-900 shadow-[0px_10px_20px_0px_rgba(0,0,0,0.12)] dark:border-white/12 dark:bg-[#2A2C2C] dark:text-white dark:shadow-[0px_10px_20px_0px_rgba(0,0,0,0.2)]", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <DropdownMenuPrimitive.SubContent ref={ref} className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== registry || $[10] !== t2) {
    t3 = <DropdownMenuPrimitive.Portal><SubMenuRegistryContext.Provider value={registry}>{t2}</SubMenuRegistryContext.Provider></DropdownMenuPrimitive.Portal>;
    $[9] = registry;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
});
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;
const DropdownMenuContent = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.Content>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>>((t0, ref) => {
  const $ = _c(14);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      sideOffset: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const sideOffset = t1 === undefined ? 4 : t1;
  const registry = useFreshSubMenuRegistry();
  let t2;
  if ($[4] !== className) {
    t2 = cn("z-100 min-w-[8rem] overflow-hidden rounded-lg border border-[#0c0c0d14] bg-white p-1 text-neutral-900 shadow-[0px_10px_20px_0px_rgba(0,0,0,0.12)] dark:border-white/12 dark:bg-[#2A2C2C] dark:text-white dark:shadow-[0px_10px_20px_0px_rgba(0,0,0,0.2)] outline-none focus:outline-none focus-visible:outline-none", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== ref || $[8] !== sideOffset || $[9] !== t2) {
    t3 = <DropdownMenuPrimitive.Content ref={ref} sideOffset={sideOffset} className={t2} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = sideOffset;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== registry || $[12] !== t3) {
    t4 = <DropdownMenuPrimitive.Portal><SubMenuRegistryContext.Provider value={registry}>{t3}</SubMenuRegistryContext.Provider></DropdownMenuPrimitive.Portal>;
    $[11] = registry;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
});
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

// Shared hook: any item (regular, checkbox, radio) that is hovered should
// close any sibling submenus at the same level. Mouse pointers only — touch
// pointers don't trigger hover-style close.
function useCloseSiblingSubmenus() {
  const $ = _c(2);
  const registry = React.useContext(SubMenuRegistryContext);
  let t0;
  if ($[0] !== registry) {
    t0 = event => {
      if (event.pointerType === "touch") {
        return;
      }
      registry?.closeAll();
    };
    $[0] = registry;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
const DropdownMenuItem = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.Item>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
  inset?: boolean;
}>((t0, ref) => {
  const $ = _c(20);
  let className;
  let inset;
  let onClick;
  let onPointerEnter;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      inset,
      onClick,
      onPointerEnter,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = inset;
    $[3] = onClick;
    $[4] = onPointerEnter;
    $[5] = props;
  } else {
    className = $[1];
    inset = $[2];
    onClick = $[3];
    onPointerEnter = $[4];
    props = $[5];
  }
  const closeSiblings = useCloseSiblingSubmenus();
  let t1;
  if ($[6] !== onClick) {
    t1 = e => {
      onClick?.(e);
      if (e.currentTarget instanceof HTMLElement) {
        e.currentTarget.blur();
      }
    };
    $[6] = onClick;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const handleClick = t1;
  const t2 = inset && "pl-8";
  let t3;
  if ($[8] !== className || $[9] !== t2) {
    t3 = cn("relative flex cursor-pointer delay-0 duration-0 select-none items-center rounded-xs px-2 py-1.5 type-body-s-regular outline-hidden focus:outline-none focus-visible:outline-none transition-colors text-neutral-900 focus:bg-neutral-50 data-[highlighted]:bg-neutral-50 data-disabled:pointer-events-none data-disabled:opacity-50 dark:text-white dark:focus:bg-[#444545] dark:data-[highlighted]:bg-[#444545]", t2, className);
    $[8] = className;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== closeSiblings || $[12] !== onPointerEnter) {
    t4 = composeEventHandlers(closeSiblings, onPointerEnter);
    $[11] = closeSiblings;
    $[12] = onPointerEnter;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== handleClick || $[15] !== props || $[16] !== ref || $[17] !== t3 || $[18] !== t4) {
    t5 = <DropdownMenuPrimitive.Item ref={ref} className={t3} onClick={handleClick} onPointerEnter={t4} {...props} />;
    $[14] = handleClick;
    $[15] = props;
    $[16] = ref;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
});
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;
const DropdownMenuCheckboxItem = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>>((t0, ref) => {
  const $ = _c(19);
  let checked;
  let children;
  let className;
  let onPointerEnter;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      checked,
      onPointerEnter,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = checked;
    $[2] = children;
    $[3] = className;
    $[4] = onPointerEnter;
    $[5] = props;
  } else {
    checked = $[1];
    children = $[2];
    className = $[3];
    onPointerEnter = $[4];
    props = $[5];
  }
  const closeSiblings = useCloseSiblingSubmenus();
  let t1;
  if ($[6] !== className) {
    t1 = cn("relative flex cursor-default select-none items-center rounded-xs py-1.5 pl-8 pr-2 type-body-s-regular outline-hidden focus:outline-none focus-visible:outline-none transition-colors text-neutral-900 focus:bg-neutral-50 data-[highlighted]:bg-neutral-50 data-disabled:pointer-events-none data-disabled:opacity-50 dark:text-white dark:focus:bg-[#444545] dark:data-[highlighted]:bg-[#444545]", className);
    $[6] = className;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== closeSiblings || $[9] !== onPointerEnter) {
    t2 = composeEventHandlers(closeSiblings, onPointerEnter);
    $[8] = closeSiblings;
    $[9] = onPointerEnter;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center"><DropdownMenuPrimitive.ItemIndicator><Check className="h-4 w-4" /></DropdownMenuPrimitive.ItemIndicator></span>;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== checked || $[13] !== children || $[14] !== props || $[15] !== ref || $[16] !== t1 || $[17] !== t2) {
    t4 = <DropdownMenuPrimitive.CheckboxItem ref={ref} className={t1} checked={checked} onPointerEnter={t2} {...props}>{t3}{children}</DropdownMenuPrimitive.CheckboxItem>;
    $[12] = checked;
    $[13] = children;
    $[14] = props;
    $[15] = ref;
    $[16] = t1;
    $[17] = t2;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  return t4;
});
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;

// Checkbox item with checkmark on the right (e.g. filter menus).
//
// Focus/highlight pattern: Radix moves focus to menu items as the user
// navigates (keyboard or pointer via data-highlighted). The background
// lives on focus/highlight only, with the browser's focus-visible
// outline suppressed so no stray blue rectangle appears around the
// highlighted row. See DESIGN.md → "Dropdown item focus" for the
// canonical pattern.
const DropdownMenuCheckboxItemRight = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>>((t0, ref) => {
  const $ = _c(21);
  let checked;
  let children;
  let className;
  let onPointerEnter;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      checked,
      onPointerEnter,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = checked;
    $[2] = children;
    $[3] = className;
    $[4] = onPointerEnter;
    $[5] = props;
  } else {
    checked = $[1];
    children = $[2];
    className = $[3];
    onPointerEnter = $[4];
    props = $[5];
  }
  const closeSiblings = useCloseSiblingSubmenus();
  let t1;
  if ($[6] !== className) {
    t1 = cn("relative flex w-full cursor-default select-none items-center justify-between gap-2 rounded-xs py-1.5 pl-2 pr-2 text-left type-body-s-regular outline-hidden focus:outline-none focus-visible:outline-none transition-colors text-neutral-900 focus:bg-neutral-50 data-[highlighted]:bg-neutral-50 data-disabled:pointer-events-none data-disabled:opacity-50 dark:text-white dark:focus:bg-[#444545] dark:data-[highlighted]:bg-[#444545]", className);
    $[6] = className;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== closeSiblings || $[9] !== onPointerEnter) {
    t2 = composeEventHandlers(closeSiblings, onPointerEnter);
    $[8] = closeSiblings;
    $[9] = onPointerEnter;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== children) {
    t3 = <span className="min-w-0 flex-1 text-left">{children}</span>;
    $[11] = children;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="flex h-4 w-4 shrink-0 items-center justify-center"><DropdownMenuPrimitive.ItemIndicator><Check className="h-4 w-4" /></DropdownMenuPrimitive.ItemIndicator></span>;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== checked || $[15] !== props || $[16] !== ref || $[17] !== t1 || $[18] !== t2 || $[19] !== t3) {
    t5 = <DropdownMenuPrimitive.CheckboxItem ref={ref} className={t1} checked={checked} onPointerEnter={t2} {...props}>{t3}{t4}</DropdownMenuPrimitive.CheckboxItem>;
    $[14] = checked;
    $[15] = props;
    $[16] = ref;
    $[17] = t1;
    $[18] = t2;
    $[19] = t3;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  return t5;
});
DropdownMenuCheckboxItemRight.displayName = "DropdownMenuCheckboxItemRight";
const DropdownMenuRadioItem = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>>((t0, ref) => {
  const $ = _c(17);
  let children;
  let className;
  let onPointerEnter;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      onPointerEnter,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = onPointerEnter;
    $[4] = props;
  } else {
    children = $[1];
    className = $[2];
    onPointerEnter = $[3];
    props = $[4];
  }
  const closeSiblings = useCloseSiblingSubmenus();
  let t1;
  if ($[5] !== className) {
    t1 = cn("relative flex cursor-default select-none items-center rounded-xs py-1.5 pl-8 pr-2 type-body-s-regular outline-hidden focus:outline-none focus-visible:outline-none transition-colors text-neutral-900 focus:bg-neutral-50 data-[highlighted]:bg-neutral-50 data-disabled:pointer-events-none data-disabled:opacity-50 dark:text-white dark:focus:bg-[#444545] dark:data-[highlighted]:bg-[#444545]", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== closeSiblings || $[8] !== onPointerEnter) {
    t2 = composeEventHandlers(closeSiblings, onPointerEnter);
    $[7] = closeSiblings;
    $[8] = onPointerEnter;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center"><DropdownMenuPrimitive.ItemIndicator><Circle className="h-2 w-2 fill-current" /></DropdownMenuPrimitive.ItemIndicator></span>;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== children || $[12] !== props || $[13] !== ref || $[14] !== t1 || $[15] !== t2) {
    t4 = <DropdownMenuPrimitive.RadioItem ref={ref} className={t1} onPointerEnter={t2} {...props}>{t3}{children}</DropdownMenuPrimitive.RadioItem>;
    $[11] = children;
    $[12] = props;
    $[13] = ref;
    $[14] = t1;
    $[15] = t2;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
});
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

// Radio item with checkmark on the right side (for sort dropdowns)
const DropdownMenuRadioItemRight = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>>((t0, ref) => {
  const $ = _c(17);
  let children;
  let className;
  let onPointerEnter;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      onPointerEnter,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = onPointerEnter;
    $[4] = props;
  } else {
    children = $[1];
    className = $[2];
    onPointerEnter = $[3];
    props = $[4];
  }
  const closeSiblings = useCloseSiblingSubmenus();
  let t1;
  if ($[5] !== className) {
    t1 = cn("relative flex cursor-pointer select-none items-center justify-between h-8 rounded px-1.5 py-1.5 type-body-s-regular outline-hidden focus:outline-none focus-visible:outline-none transition-colors text-neutral-900 focus:bg-neutral-50 data-[highlighted]:bg-neutral-50 data-disabled:pointer-events-none data-disabled:opacity-50 dark:text-white dark:focus:bg-[#444545] dark:data-[highlighted]:bg-[#444545]", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== closeSiblings || $[8] !== onPointerEnter) {
    t2 = composeEventHandlers(closeSiblings, onPointerEnter);
    $[7] = closeSiblings;
    $[8] = onPointerEnter;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <DropdownMenuPrimitive.ItemIndicator className="ml-3 flex h-4 w-4 items-center justify-center"><Check className="h-4 w-4" /></DropdownMenuPrimitive.ItemIndicator>;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== children || $[12] !== props || $[13] !== ref || $[14] !== t1 || $[15] !== t2) {
    t4 = <DropdownMenuPrimitive.RadioItem ref={ref} className={t1} onPointerEnter={t2} {...props}>{children}{t3}</DropdownMenuPrimitive.RadioItem>;
    $[11] = children;
    $[12] = props;
    $[13] = ref;
    $[14] = t1;
    $[15] = t2;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
});
DropdownMenuRadioItemRight.displayName = "DropdownMenuRadioItemRight";
const DropdownMenuLabel = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.Label>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
  inset?: boolean;
}>((t0, ref) => {
  const $ = _c(11);
  let className;
  let inset;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      inset,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = inset;
    $[3] = props;
  } else {
    className = $[1];
    inset = $[2];
    props = $[3];
  }
  const t1 = inset && "pl-8";
  let t2;
  if ($[4] !== className || $[5] !== t1) {
    t2 = cn("px-2 py-1.5 type-page-title-m", t1, className);
    $[4] = className;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== props || $[8] !== ref || $[9] !== t2) {
    t3 = <DropdownMenuPrimitive.Label ref={ref} className={t2} {...props} />;
    $[7] = props;
    $[8] = ref;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
});
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;
const DropdownMenuSeparator = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.Separator>, React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("-mx-1 my-1 h-px bg-neutral-100 dark:bg-[#444545]", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <DropdownMenuPrimitive.Separator ref={ref} className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;
const DropdownMenuShortcut = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("ml-auto text-xs tracking-widest opacity-60", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <span className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";
export { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuCheckboxItem, DropdownMenuCheckboxItemRight, DropdownMenuRadioItem, DropdownMenuRadioItemRight, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuGroup, DropdownMenuPortal, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuRadioGroup };